import React, { useState } from "react";
import Icon from "components/AppIcon";


const SECTORS = ["All Sectors", "Energy", "Transport", "Digital Infrastructure", "Water"];
const REGIONS = ["All Regions", "UK", "Europe", "North America", "Asia Pacific", "Africa"];
const STAGES = ["All Stages", "Sourcing", "Screening", "Diligence", "IC Review", "Execution", "Asset Management"];
const RISK_RATINGS = ["All", "Low", "Medium", "High"];
const IMPACT_THEMES = ["Climate", "Social Infrastructure", "Digital Access", "Water Security", "Clean Energy", "Mobility"];
const SORT_OPTIONS = [
  { value: "relevance", label: "Relevance" },
  { value: "irr_desc", label: "IRR: High to Low" },
  { value: "irr_asc", label: "IRR: Low to High" },
  { value: "ticket_desc", label: "Ticket Size: High to Low" },
  { value: "ticket_asc", label: "Ticket Size: Low to High" },
  { value: "stage", label: "Stage" },
];

export default function FilterToolbar({ filters, onFilterChange, onSortChange, sortBy, resultCount, onMobileFilterToggle }) {
  const [activeImpact, setActiveImpact] = useState(filters?.impactThemes || []);

  const handleImpactToggle = (theme) => {
    const updated = activeImpact?.includes(theme)
      ? activeImpact?.filter((t) => t !== theme)
      : [...activeImpact, theme];
    setActiveImpact(updated);
    onFilterChange({ ...filters, impactThemes: updated });
  };

  const handleChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const activeFilterCount = [
    filters?.sector !== "All Sectors",
    filters?.region !== "All Regions",
    filters?.stage !== "All Stages",
    filters?.riskRating !== "All",
    (filters?.impactThemes || [])?.length > 0,
    filters?.irrMin > 0 || filters?.irrMax < 30,
  ]?.filter(Boolean)?.length;

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-5 shadow-xs">
      {/* Top row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px]">
          <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search opportunities..."
            value={filters?.search || ""}
            onChange={(e) => handleChange("search", e?.target?.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {/* Desktop filters */}
        <div className="hidden md:flex flex-wrap items-center gap-2">
          <select
            value={filters?.sector}
            onChange={(e) => handleChange("sector", e?.target?.value)}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
          >
            {SECTORS?.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select
            value={filters?.region}
            onChange={(e) => handleChange("region", e?.target?.value)}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
          >
            {REGIONS?.map((r) => <option key={r}>{r}</option>)}
          </select>
          <select
            value={filters?.stage}
            onChange={(e) => handleChange("stage", e?.target?.value)}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
          >
            {STAGES?.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select
            value={filters?.riskRating}
            onChange={(e) => handleChange("riskRating", e?.target?.value)}
            className="text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
          >
            {RISK_RATINGS?.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>

        {/* Mobile filter button */}
        <button
          onClick={onMobileFilterToggle}
          className="md:hidden flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground"
        >
          <Icon name="SlidersHorizontal" size={16} />
          Filters
          {activeFilterCount > 0 && (
            <span className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-semibold" style={{ background: "var(--color-primary)" }}>
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e?.target?.value)}
          className="text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer ml-auto"
        >
          {SORT_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
        </select>
      </div>
      {/* IRR Range */}
      <div className="hidden md:flex items-center gap-4 mb-4">
        <span className="text-sm text-muted-foreground font-caption whitespace-nowrap">Target IRR:</span>
        <div className="flex items-center gap-2 flex-1 max-w-xs">
          <span className="text-sm font-data text-foreground">{filters?.irrMin}%</span>
          <input
            type="range" min={0} max={30} step={1}
            value={filters?.irrMin}
            onChange={(e) => handleChange("irrMin", Number(e?.target?.value))}
            className="flex-1 accent-primary"
          />
          <input
            type="range" min={0} max={30} step={1}
            value={filters?.irrMax}
            onChange={(e) => handleChange("irrMax", Number(e?.target?.value))}
            className="flex-1 accent-primary"
          />
          <span className="text-sm font-data text-foreground">{filters?.irrMax}%</span>
        </div>
      </div>
      {/* Impact themes */}
      <div className="hidden md:flex flex-wrap items-center gap-2 mb-3">
        <span className="text-sm text-muted-foreground font-caption">Impact:</span>
        {IMPACT_THEMES?.map((theme) => (
          <button
            key={theme}
            onClick={() => handleImpactToggle(theme)}
            className={`px-3 py-1 rounded-full text-xs font-caption font-medium border transition-all duration-200 ${
              activeImpact?.includes(theme)
                ? "border-primary bg-primary text-white" :"border-border bg-background text-muted-foreground hover:border-primary hover:text-primary"
            }`}
          >
            {theme}
          </button>
        ))}
      </div>
      {/* Results count + clear */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground font-caption">
          <span className="font-semibold text-foreground font-data">{resultCount}</span> opportunities found
        </p>
        {activeFilterCount > 0 && (
          <button
            onClick={() => {
              setActiveImpact([]);
              onFilterChange({ sector: "All Sectors", region: "All Regions", stage: "All Stages", riskRating: "All", impactThemes: [], irrMin: 0, irrMax: 30, search: "" });
            }}
            className="text-sm text-primary hover:underline font-caption flex items-center gap-1"
          >
            <Icon name="X" size={14} />
            Clear all filters
          </button>
        )}
      </div>
    </div>
  );
}